import { createContextApi, CACHE_POLICY, createCursorPagedEndpoint, providesOne, providesList } from '@quickbee/store';
import { useState, useCallback, useEffect, useRef } from 'react';

// src/index.ts
var REPORTING_TAGS = ["Report", "Dashboard"];
function queryParams(entries) {
  const params = {};
  for (const [key, value] of Object.entries(entries)) {
    if (value !== void 0 && value !== null && value !== "") params[key] = value;
  }
  return params;
}
var reportingEndpoints = (build) => ({
  /**
   * The caller's own reports — savers see reports *about* them, staff see
   * reports they *requested*. The scope is derived from the token, so there is
   * no customer-id argument, and no `type`/`status` filter exists server-side.
   */
  listReports: build.query({
    query: ({ size, cursor, sort }) => ({
      url: "/reports",
      params: queryParams({ size, cursor, sort })
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Report", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getReport: build.query({
    query: (id) => ({ url: `/reports/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Report", id),
    // The polling loop owns freshness; a stale cached "GENERATING" would hide completion.
    keepUnusedDataFor: 0
  }),
  /**
   * Answers `202 Accepted`. `draftId` is the caller-stable half of the
   * idempotency key — it must survive a re-render and a retry, so derive it from
   * the form draft, not from `Date.now()`.
   */
  createReport: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/reports",
      method: "POST",
      body,
      // Report generation is expensive; a double submit should not queue two runs.
      idempotencyKey: `report:${body.type}:${body.format}:${draftId}`
    }),
    invalidatesTags: [{ type: "Report", id: "LIST" }]
  }),
  /**
   * Mints a presigned artifact URL, valid for at most five minutes and never
   * longer than the report's own retention. Not cached (§13.1): hold it in
   * component state for the duration of the click.
   *
   * The URL points at object storage, not at the gateway — hand it to the
   * browser (`window.open`, an `<a download>`), never back to `@quickbee/api-client`,
   * which would attach `Authorization` and be rejected by the presign signature.
   *
   * Fails with `REPORT_NOT_READY`, `REPORT_EXPIRED`, `REPORT_FAILED` or
   * `REPORT_STATE_INVALID` rather than a generic 409 — all four are mapped in
   * `errorCodeMap` and are worth distinct copy.
   */
  getReportDownloadUrl: build.query({
    query: (id) => ({ url: `/reports/${id}/download` }),
    keepUnusedDataFor: 0
  }),
  // ─── Dashboards (read model; §10.4 report policy) ───────────────────────────
  /**
   * The daily mark-to-market **series** for one customer — a paged list of
   * per-day rows, not a single summary. `customerId`, `from` and `to` are all
   * required; the window is capped at 366 days and may not end in the future
   * (`isValidMtmRange` mirrors both rules). A saver's `customerId` is ignored
   * and forced to their own subject server-side.
   */
  getHoldingsMtm: build.query({
    query: ({ customerId, from, to, size, cursor, sort }) => ({
      url: "/reports/holdings/mtm",
      params: queryParams({ customerId, from, to, size, cursor, sort })
    }),
    ...createCursorPagedEndpoint(),
    providesTags: () => [{ type: "Dashboard", id: "holdings-mtm" }],
    keepUnusedDataFor: CACHE_POLICY.report.keepUnusedDataFor
  }),
  /**
   * Current holding and valuation for one customer. `customerId` is required.
   *
   * A customer with no projection row answers `404`, which for a newly onboarded
   * saver is the normal case rather than a fault — branch on it to render an
   * empty state.
   */
  getPortfolio: build.query({
    query: ({ customerId }) => ({ url: "/reports/portfolio", params: queryParams({ customerId }) }),
    providesTags: (_r, _e, { customerId }) => [
      { type: "Dashboard", id: `portfolio:${customerId}` }
    ],
    keepUnusedDataFor: CACHE_POLICY.financialBalance.keepUnusedDataFor
  }),
  /** Per-scheme rollup for one `yyyy-MM` period. Requires `reports:read:tenant`. */
  getSchemePerformance: build.query({
    query: ({ period, schemeId, size, cursor, sort }) => ({
      url: "/reports/scheme-performance",
      params: queryParams({ period, schemeId, size, cursor, sort })
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (_r, _e, { period }) => [
      { type: "Dashboard", id: `scheme-performance:${period}` }
    ],
    keepUnusedDataFor: CACHE_POLICY.report.keepUnusedDataFor
  }),
  /**
   * Agent commission accruals for one period. An `AGENT` sees only their own
   * rows whatever `agentId` says; RM/ops/tenant-admin may filter.
   *
   * This endpoint exists and is wired — an earlier revision of this file claimed
   * reporting-service had removed it, which was wrong. What is genuinely missing
   * is agent *name* attribution: rows carry `agentId` and nothing else.
   */
  getCommissions: build.query({
    query: ({ period, agentId, branchId, size, cursor, sort }) => ({
      url: "/reports/commissions",
      params: queryParams({ period, agentId, branchId, size, cursor, sort })
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (_r, _e, { period }) => [
      { type: "Dashboard", id: `commissions:${period}` }
    ],
    keepUnusedDataFor: CACHE_POLICY.report.keepUnusedDataFor
  }),
  /**
   * Ops-console transaction activity for one period. The only endpoint in this
   * context with a real keyset cursor, so it is the only one where "Load more"
   * genuinely pages.
   *
   * `type` must be a `TransactionType`; any other string is a 400 listing the
   * accepted values.
   */
  getActivity: build.query({
    query: ({ period, branchId, type, size, cursor, sort }) => ({
      url: "/reports/activity",
      params: queryParams({ period, branchId, type, size, cursor, sort })
    }),
    ...createCursorPagedEndpoint(),
    providesTags: (_r, _e, { period }) => [
      { type: "Dashboard", id: `activity:${period}` }
    ],
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  /** Tenant KYC funnel counts for one period. Requires `reports:kyc:tenant`. */
  getKycMetrics: build.query({
    query: ({ period }) => ({ url: "/reports/kyc-metrics", params: queryParams({ period }) }),
    providesTags: (_r, _e, { period }) => [
      { type: "Dashboard", id: `kyc-metrics:${period}` }
    ],
    keepUnusedDataFor: CACHE_POLICY.report.keepUnusedDataFor
  })
});

// src/pollSchedule.ts
var REPORT_POLL_BACKOFF_MS = [2e3, 5e3, 1e4, 2e4, 3e4];
var REPORT_POLL_CEILING_MS = 5 * 6e4;
var INITIAL_POLL_SCHEDULE = {
  attempts: 0,
  elapsedMs: 0,
  timedOut: false
};
function reportPollDelayMs(attempts) {
  const step = Math.min(Math.max(attempts, 0), REPORT_POLL_BACKOFF_MS.length - 1);
  return REPORT_POLL_BACKOFF_MS[step];
}
function advancePollSchedule(state, waitedMs) {
  if (state.timedOut) return state;
  const elapsedMs = state.elapsedMs + Math.max(waitedMs, 0);
  if (elapsedMs >= REPORT_POLL_CEILING_MS) {
    return { attempts: state.attempts, elapsedMs, timedOut: true };
  }
  return { attempts: state.attempts + 1, elapsedMs, timedOut: false };
}
function isPollingActive(gate) {
  return gate.pending && gate.enabled && gate.visible && !gate.timedOut;
}
function pollingIntervalFor(state, active) {
  return active ? reportPollDelayMs(state.attempts) : 0;
}

// src/types.ts
var REPORT_STATUSES = [
  "REQUESTED",
  "GENERATING",
  "GENERATED",
  "FAILED",
  "EXPIRED"
];
var REPORT_TYPES = [
  "ACCOUNT_STATEMENT",
  "HOLDING_REPORT",
  "TAX_CERTIFICATE",
  "KYC_REPORT",
  "SCHEME_PERFORMANCE",
  "COMMISSION_STATEMENT"
];
var REPORT_FORMATS = ["PDF", "CSV", "XLSX"];
var TRANSACTION_TYPES = [
  "GOLD_PURCHASE",
  "SIP_INSTALLMENT",
  "BONUS_CREDIT",
  "REDEMPTION",
  "REFUND",
  "ADJUSTMENT"
];
var REPORT_FAILURE_REASONS = [
  "NO_DATA_IN_RANGE",
  "INVALID_REPORT_SPEC",
  "GENERATION_TIMEOUT",
  "GENERATION_FAILED"
];

// src/reportSpec.ts
var REPORT_TYPE_FORMATS = {
  ACCOUNT_STATEMENT: ["PDF", "CSV", "XLSX"],
  HOLDING_REPORT: ["PDF", "CSV", "XLSX"],
  TAX_CERTIFICATE: ["PDF"],
  KYC_REPORT: ["PDF", "CSV"],
  SCHEME_PERFORMANCE: ["PDF", "CSV", "XLSX"],
  COMMISSION_STATEMENT: ["PDF", "CSV", "XLSX"]
};
var CUSTOMER_SCOPED_REPORT_TYPES = {
  ACCOUNT_STATEMENT: true,
  HOLDING_REPORT: true,
  TAX_CERTIFICATE: true,
  KYC_REPORT: false,
  SCHEME_PERFORMANCE: false,
  COMMISSION_STATEMENT: false
};
var MAX_MTM_RANGE_DAYS = 366;
var PERIOD_FLOOR = "2020-01";
function supportsFormat(type, format) {
  return REPORT_TYPE_FORMATS[type].includes(format);
}
function formatsFor(type) {
  return REPORT_TYPE_FORMATS[type];
}
function isCustomerScoped(type) {
  return CUSTOMER_SCOPED_REPORT_TYPES[type];
}
function isPendingReport(status) {
  return status === "REQUESTED" || status === "GENERATING";
}
function isTerminalReport(status) {
  return status !== void 0 && REPORT_STATUSES.includes(status) && !isPendingReport(status);
}
function isValidPeriod(period, now = /* @__PURE__ */ new Date()) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) return false;
  return period >= PERIOD_FLOOR && period <= currentPeriod(now);
}
function currentPeriod(now = /* @__PURE__ */ new Date()) {
  return now.toISOString().slice(0, 7);
}
function isValidMtmRange(from, to, now = /* @__PURE__ */ new Date()) {
  if (!isIsoDate(from) || !isIsoDate(to)) return false;
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || start > end) return false;
  if (end > Date.parse(`${now.toISOString().slice(0, 10)}T00:00:00Z`)) return false;
  const days = (end - start) / 864e5 + 1;
  return days <= MAX_MTM_RANGE_DAYS;
}
function isIsoDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = Date.parse(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed)) return false;
  return new Date(parsed).toISOString().slice(0, 10) === value;
}

// src/useReportPolling.ts
function isDocumentVisible() {
  return typeof document === "undefined" || document.visibilityState === "visible";
}
function useReportPolling(options) {
  const { status, isPending, enabled = true } = options;
  const pending = isPending ?? (status === void 0 || isPendingReport(status));
  const [schedule, setSchedule] = useState(INITIAL_POLL_SCHEDULE);
  const [visible, setVisible] = useState(isDocumentVisible);
  const [runId, setRunId] = useState(0);
  const reset = useCallback(() => {
    setSchedule(INITIAL_POLL_SCHEDULE);
    setRunId((current) => current + 1);
  }, []);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibility = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);
  useEffect(() => {
    if (!pending) setSchedule(INITIAL_POLL_SCHEDULE);
  }, [pending]);
  const active = isPollingActive({ pending, enabled, visible, timedOut: schedule.timedOut });
  const fired = useRef(false);
  useEffect(() => {
    if (!active) return;
    const startedAt = Date.now();
    fired.current = false;
    const timer = setTimeout(() => {
      fired.current = true;
      setSchedule((current) => advancePollSchedule(current, Date.now() - startedAt));
    }, reportPollDelayMs(schedule.attempts));
    return () => {
      clearTimeout(timer);
      if (fired.current) return;
      const waited = Date.now() - startedAt;
      if (waited > 0) {
        setSchedule(
          (current) => current.timedOut ? current : { ...current, elapsedMs: current.elapsedMs + waited }
        );
      }
    };
  }, [active, schedule.attempts, runId]);
  return {
    pollingInterval: pollingIntervalFor(schedule, active),
    timedOut: schedule.timedOut,
    attempts: schedule.attempts,
    isPolling: active,
    reset
  };
}

// src/index.ts
var reportingApi = createContextApi({
  context: "reporting",
  reducerPath: "reportingApi",
  tagTypes: REPORTING_TAGS,
  mocks: () => import('./mocks-VSXT45TR.js').then((module) => module.reportingMocks),
  endpoints: reportingEndpoints
});
var {
  useListReportsQuery,
  useGetReportQuery,
  useCreateReportMutation,
  useGetReportDownloadUrlQuery,
  // A download URL is minted on click, not on render — the lazy hook is the one
  // most screens want.
  useLazyGetReportDownloadUrlQuery,
  useGetHoldingsMtmQuery,
  useGetPortfolioQuery,
  useGetSchemePerformanceQuery,
  useGetCommissionsQuery,
  useGetActivityQuery,
  useGetKycMetricsQuery
} = reportingApi;

export { CUSTOMER_SCOPED_REPORT_TYPES, INITIAL_POLL_SCHEDULE, MAX_MTM_RANGE_DAYS, PERIOD_FLOOR, REPORTING_TAGS, REPORT_FAILURE_REASONS, REPORT_FORMATS, REPORT_POLL_BACKOFF_MS, REPORT_POLL_CEILING_MS, REPORT_STATUSES, REPORT_TYPES, REPORT_TYPE_FORMATS, TRANSACTION_TYPES, advancePollSchedule, currentPeriod, formatsFor, isCustomerScoped, isPendingReport, isPollingActive, isTerminalReport, isValidMtmRange, isValidPeriod, pollingIntervalFor, reportPollDelayMs, reportingApi, supportsFormat, useCreateReportMutation, useGetActivityQuery, useGetCommissionsQuery, useGetHoldingsMtmQuery, useGetKycMetricsQuery, useGetPortfolioQuery, useGetReportDownloadUrlQuery, useGetReportQuery, useGetSchemePerformanceQuery, useLazyGetReportDownloadUrlQuery, useListReportsQuery, useReportPolling };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map